import { forwardRef, type ReactNode } from 'react';
import { Tooltip } from '@odo/shared-ui';

// A small badge on an icon (bolt, pen) gets a halo in the button's current fill, hover included.
const COMPOSER_ICON_BADGE_CLASS = 'rounded-full bg-[var(--composer-icon-bg)] transition-colors';

interface ComposerIconButtonProps {
    label: string;
    // Set only for buttons that toggle a popover; plain actions leave it undefined.
    active?: boolean;
    // Set only for on/off toggles; shows the active tone without popover semantics.
    pressed?: boolean;
    // Keeps the button hoverable (unlike `disabled`) so the tooltip can say why it is off.
    disabledReason?: string;
    onClick: () => void;
    children: ReactNode;
}

function buttonTone(active: boolean | undefined, disabled: boolean): string {
    if (disabled)
        return 'text-gray-300 cursor-not-allowed [--composer-icon-bg:theme(colors.white)]';
    if (active) return 'text-gray-900 [--composer-icon-bg:theme(colors.gray.100)]';
    return 'text-gray-500 hover:text-gray-800 [--composer-icon-bg:theme(colors.white)] hover:[--composer-icon-bg:theme(colors.gray.100)]';
}

const ComposerIconButton = forwardRef<HTMLButtonElement, ComposerIconButtonProps>(
    function ComposerIconButton(
        { label, active, pressed, disabledReason, onClick, children },
        ref
    ) {
        const togglesPopover = active !== undefined;
        const disabled = disabledReason !== undefined;
        // No tooltip while its popover is open: it would sit on top of it.
        return (
            <Tooltip content={disabled ? `${label}\n${disabledReason}` : label} disabled={active}>
                <button
                    ref={ref}
                    type="button"
                    onClick={disabled ? undefined : onClick}
                    className={`w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center bg-[var(--composer-icon-bg)] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${buttonTone(
                        active || pressed,
                        disabled
                    )}`}
                    aria-label={label}
                    aria-disabled={disabled || undefined}
                    aria-haspopup={togglesPopover ? 'dialog' : undefined}
                    aria-expanded={togglesPopover ? active : undefined}
                    aria-pressed={pressed}
                >
                    {children}
                </button>
            </Tooltip>
        );
    }
);

export { ComposerIconButton, COMPOSER_ICON_BADGE_CLASS };
